import { apiClient } from '@/api/client';
import type { ApiResponseWrapper } from '@/types/api.types';
import type { EmploymentFacet, EmploymentRow } from './employment.service';
import type { HrCaseKind, HrCaseListItem, HrCaseStatus } from './hrCases.service';
import type { HrDocument, HrDocumentStatus } from './hrDocuments.service';
import type { Movement } from './movements.service';
import type { ProbationBucket, ProbationCase, ProbationSummary } from './probation.service';

/** Status of a company member who has no employment record yet. */
export const NOT_SET_UP = 'not_set_up';

export interface HrPage<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export interface HrPersonRef {
  companyId: string;
  userId: string;
}

export interface HrPersonRow extends Omit<EmploymentRow, 'currentAssignment'> {
  companyName: string | null;
  currentAssignment: {
    departmentName: string | null;
    jobTitleName: string | null;
    employmentTypeName: string | null;
  } | null;
}

export interface HrFacet extends EmploymentFacet {
  companyId: string;
}

export interface HrPeoplePage extends HrPage<HrPersonRow> {
  notSetUpTotal: number;
  facets: { departments: HrFacet[]; jobTitles: HrFacet[]; employmentTypes: HrFacet[] };
}

export interface HrPeopleFilters {
  search?: string;
  lifecycleStatus?: string;
  departmentId?: string;
  jobTitleId?: string;
  employmentTypeId?: string;
  setUpOnly?: boolean;
}

export interface HrSavedViewFilters {
  search?: string;
  lifecycleStatus?: string;
  departmentId?: string;
  jobTitleId?: string;
  employmentTypeId?: string;
}

export interface HrSavedView {
  _id: string;
  name: string;
  companyId?: string;
  filters: HrSavedViewFilters;
}

export interface HrSetUpResult extends HrPersonRef {
  status: 'set_up' | 'failed';
  reason?: string;
}

export interface HrOverview {
  notSetUp?: number;
  probation?: { overdue: number; due: number };
  jobChanges?: { awaitingApproval: number; blocked: number; failed: number };
  lettersAwaitingSignature?: number;
  overdueAcknowledgements?: number;
  openCases?: Partial<Record<HrCaseKind, number>>;
}

export type HrMovementRow = Movement & {
  companyName: string | null;
  employee: { name: string; employeeId: string } | null;
};
export type HrProbationRow = ProbationCase & { companyName: string | null };
export type HrDocumentRow = HrDocument & { companyName: string | null };
export type HrCaseRow = HrCaseListItem & { companyId: string; companyName: string | null };

/** The API takes a comma-separated list; `undefined` means every allowed company. */
function companyParam(companyIds?: string[]): { companyIds?: string } {
  return companyIds?.length ? { companyIds: companyIds.join(',') } : {};
}

async function read<T>(path: string, params: Record<string, unknown> = {}): Promise<T> {
  const { data } = await apiClient.get<ApiResponseWrapper<T>>(`/hr/workspace/${path}`, { params });
  return data.data;
}

export function getHrOverview(companyIds?: string[]): Promise<HrOverview> {
  return read('overview', companyParam(companyIds));
}

export function listHrPeople(
  companyIds: string[] | undefined,
  page: number,
  limit: number,
  filters: HrPeopleFilters = {}
): Promise<HrPeoplePage> {
  return read('people', { ...companyParam(companyIds), page, limit, ...filters });
}

export async function listNotSetUpPeople(
  companyIds: string[] | undefined,
  search?: string
): Promise<Array<HrPersonRef & { name: string }>> {
  const { data } = await read<{ data: Array<HrPersonRef & { name: string }> }>(
    'people/not-set-up',
    { ...companyParam(companyIds), ...(search && { search }) }
  );
  return data;
}

export async function setUpHrPeople(
  people: HrPersonRef[],
  status: 'active' | 'probation'
): Promise<HrSetUpResult[]> {
  const { data } = await apiClient.post<ApiResponseWrapper<{ results: HrSetUpResult[] }>>(
    '/hr/workspace/people/set-up',
    { people, status }
  );
  return data.data.results;
}

export async function exportHrPeople(people: HrPersonRef[]): Promise<Blob> {
  const { data } = await apiClient.post(
    '/hr/workspace/people/export',
    { people },
    { responseType: 'blob' }
  );
  return data;
}

export async function listHrSavedViews(): Promise<HrSavedView[]> {
  return read('people/views');
}

export async function createHrSavedView(input: {
  name: string;
  companyId?: string;
  filters: HrSavedViewFilters;
}): Promise<HrSavedView> {
  const { data } = await apiClient.post<ApiResponseWrapper<HrSavedView>>(
    '/hr/workspace/people/views',
    input
  );
  return data.data;
}

export async function updateHrSavedView(
  viewId: string,
  input: { filters: HrSavedViewFilters }
): Promise<HrSavedView> {
  const { data } = await apiClient.patch<ApiResponseWrapper<HrSavedView>>(
    `/hr/workspace/people/views/${viewId}`,
    input
  );
  return data.data;
}

export async function deleteHrSavedView(viewId: string): Promise<void> {
  await apiClient.delete(`/hr/workspace/people/views/${viewId}`);
}

export function listHrMovements(
  companyIds: string[] | undefined,
  params: { page?: number; limit?: number; status?: string } = {}
): Promise<HrPage<HrMovementRow>> {
  return read('movements', { ...companyParam(companyIds), ...params });
}

export function listHrProbation(
  companyIds: string[] | undefined,
  params: { bucket?: ProbationBucket; page?: number; limit?: number } = {}
): Promise<HrPage<HrProbationRow>> {
  return read('probation', { ...companyParam(companyIds), ...params });
}

export function getHrProbationSummary(companyIds?: string[]): Promise<ProbationSummary> {
  return read('probation/summary', companyParam(companyIds));
}

export function listHrDocuments(
  companyIds: string[] | undefined,
  params: { status?: HrDocumentStatus; page?: number; limit?: number } = {}
): Promise<HrPage<HrDocumentRow>> {
  return read('documents', { ...companyParam(companyIds), ...params });
}

export function listHrCases(
  companyIds: string[] | undefined,
  params: { kind: HrCaseKind; status?: HrCaseStatus; page?: number; limit?: number }
): Promise<HrPage<HrCaseRow>> {
  return read('cases', { ...companyParam(companyIds), ...params });
}
